import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import axios from 'axios';
import { MaterialModule, JackBaseComponentModule, jkLib, login } from 'jk-service';

type TimerSegment = 'work' | 'rest';
type TimerStatus = 'ready' | 'running' | 'paused' | 'waiting';
export type SpeechKind = 'work' | 'rest';

const WORK_MS = 25 * 60 * 1000;
const REST_MS = 5 * 60 * 1000;

type SaveState = 'idle' | 'editing' | 'saving' | 'saved' | 'failed';
const MINUTES_TO_MS = 60 * 1000;

@Component({
  selector: 'app-pomodoro',
  standalone: true,
  imports: [FormsModule,MaterialModule,JackBaseComponentModule],
  templateUrl: './pomodoro.component.html',
  styleUrl: './pomodoro.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PomodoroComponent implements OnInit, OnDestroy {
  //readonly speech = inject(SpeechService);
  readonly errorMessage = signal('');

  readonly pomodoroEnabled = signal(true);
  readonly showSetup = signal(false);
  readonly workMinutes = signal(25);
  readonly restMinutes = signal(5);
  segment = signal<TimerSegment>('work');
  readonly status = signal<TimerStatus>('ready');
  readonly remainingMs = signal(WORK_MS);

  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private savingPromise: Promise<void> | null = null;
  private revision = 0;
  private dirty = false;

  alerting = signal(false);
  apiKey = signal('');
  settingsMessage = signal('');
  savingKey = signal(false);
  testingSpeech = signal(false);

  displayTime = computed(() => {
    const totalSeconds = Math.max(0, Math.ceil(this.remainingMs() / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  });

  segmentLabel = computed(() => this.segment() === 'work' ? '專注工作' : '休息一下');
  startLabel = computed(() => {
    if (this.status() === 'waiting' && this.segment() === 'rest') return '休息';
    if (this.status() === 'waiting') return '開始工作';
    return this.segment() === 'work' ? '工作' : '休息';
  });

  private ticker: ReturnType<typeof setInterval> | null = null;
  private deadline = 0;

  ngOnInit(): void {
    //this.refreshSettings().catch(() => {});
    this.ticker = setInterval(() => this.tick(), 250);
  }

  ngOnDestroy(): void {
    if (this.ticker !== null) clearInterval(this.ticker);
    if (this.autoSaveTimer !== null) clearTimeout(this.autoSaveTimer);
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    //this.flushAutoSave().catch(() => {});
  }



  private tick(): void {
    if (this.status() !== 'running') return;
    const remaining = this.deadline - Date.now();
    if (remaining > 0) {
      this.remainingMs.set(remaining);
      return;
    }
    const completed = this.segment();
    const next: TimerSegment = completed === 'work' ? 'rest' : 'work';
    this.segment.set(next);
    this.remainingMs.set(this.segmentDurationMs());
    this.status.set('waiting');
    this.announce(completed as SpeechKind).catch(() => {});
  }

  private showBrowserNotification(kind: SpeechKind): void {
    if (!('Notification' in window) || Notification.permission !== 'granted')
      return;
    const body =
      kind === 'work'
        ? '工作時間結束，休息五分鐘'
        : '休息時間結束，請手動開始下一輪';
    new Notification('LockHost 番茄鐘', { body });
  }
  async requestNotificationPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission().catch(() => 'denied');
    }
  }

  async start(): Promise<void> {
    if (!this.pomodoroEnabled() || this.status() === 'running') return;
    await this.requestNotificationPermission();
    const duration =
      this.status() === 'paused'
        ? this.remainingMs()
        : this.segmentDurationMs();
    this.remainingMs.set(duration);
    this.deadline = Date.now() + duration;
    this.status.set('running');
  }

  togglePause(): void {
    if (this.status() === 'running') {
      this.remainingMs.set(Math.max(0, this.deadline - Date.now()));
      this.status.set('paused');
      return;
    }
    if (this.status() === 'paused') {
      this.deadline = Date.now() + this.remainingMs();
      this.status.set('running');
    }
  }

  reset(): void {
    this.segment.set('work');
    this.status.set('ready');
    this.remainingMs.set(this.workMinutes() * MINUTES_TO_MS);
    this.deadline = 0;
  }


  private segmentDurationMs(): number {
    return (
      (this.segment() === 'work' ? this.workMinutes() : this.restMinutes()) *
      MINUTES_TO_MS
    );
  }

  private normalizeMinutes(value: number): number {
    return Math.min(120, Math.max(1, Math.floor(Number(value) || 1)));
  }
  beepFallback(): void {
    try {
      const AudioContextClass = window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;
      const context = new AudioContextClass();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.22, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 1.2);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 1.2);
      oscillator.addEventListener('ended', () => context.close().catch(() => {}));
    } catch {
      // Nothing else can safely alert with audio.
    }
  }

  private async playSpeech(kind: SpeechKind): Promise<void> {
    const message = kind === 'work'
      ? `工作時間結束，休息${this.restMinutes()}分鐘`
      : '休息時間結束，請手動開始下一輪';
    const config = jkLib.axiosConfigInit(
      login.host,
      '/gemini-agent/tts',
      'get',
      { message },
      {},
    );
    config.responseType = 'blob';
    try {
      const response = await axios(config);
      const audioUrl = URL.createObjectURL(response.data);
      try {
        const audio = new Audio(audioUrl);
        await new Promise<void>((resolve, reject) => {
          audio.addEventListener('ended', () => resolve(), { once: true });
          audio.addEventListener(
            'error',
            () => reject(new Error('TTS audio playback failed')),
            { once: true },
          );
          audio.play().catch(reject);
        });
      } finally {
        URL.revokeObjectURL(audioUrl);
      }
    } catch {
      this.beepFallback();
    }
  }
  async testSpeech(): Promise<void> {
    this.settingsMessage.set('');
    this.testingSpeech.set(true);
    try {
      await this.playSpeech('work');
      this.settingsMessage.set('已播放測試語音。');
    } catch (error) {
      this.settingsMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.testingSpeech.set(false);
    }
  }
  setPomodoroEnabled(enabled: boolean): void {
    this.pomodoroEnabled.set(enabled);
    if (!enabled) this.reset();
  }

  setWorkMinutes(value: number): void {
    this.workMinutes.set(this.normalizeMinutes(value));
    if (this.status() === 'ready' && this.segment() === 'work')
      this.remainingMs.set(this.segmentDurationMs());
  }

  setRestMinutes(value: number): void {
    this.restMinutes.set(this.normalizeMinutes(value));
    if (this.status() === 'ready' && this.segment() === 'rest')
      this.remainingMs.set(this.segmentDurationMs());
  }
  async announce(kind: SpeechKind): Promise<void> {
    this.showBrowserNotification(kind);
    //await this.focusTauriWindow();
    await this.playSpeech(kind);
  }


}
